/**
 * Labels and colours for agent status time charts (matches Worker rollups).
 */
export const STATUS_TIME_CATEGORY_ORDER = [
  'available',
  'talking',
  'wrap',
  'paused',
  'unavailable',
  'other',
] as const;

export type StatusTimeCategoryKey = (typeof STATUS_TIME_CATEGORY_ORDER)[number];

export const STATUS_TIME_CATEGORY_META: Record<
  StatusTimeCategoryKey,
  { label: string; color: string }
> = {
  available: { label: 'Available', color: '#22c55e' },
  talking: { label: 'Talking', color: '#ef4444' },
  wrap: { label: 'Wrap', color: '#f97316' },
  paused: { label: 'Paused', color: '#a855f7' },
  unavailable: { label: 'Unavailable', color: '#eab308' },
  other: { label: 'Other', color: '#64748b' },
};

export function formatStatusTimeSeconds(sec: number): string {
  const s = Math.max(0, Math.floor(sec));
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

export function categoryLabel(key: string): string {
  const meta = STATUS_TIME_CATEGORY_META[key as StatusTimeCategoryKey];
  return meta?.label ?? key.charAt(0).toUpperCase() + key.slice(1);
}

export function categoryColor(key: string): string {
  const meta = STATUS_TIME_CATEGORY_META[key as StatusTimeCategoryKey];
  return meta?.color ?? '#64748b';
}

export type StatusDetailEntry = {
  parent_category: string;
  detail_key: string;
  detail_label: string;
  seconds: number;
};

const DETAIL_PARENT_ORDER: Record<string, number> = {
  available: 0,
  talking: 1,
  wrap: 2,
  paused: 3,
  unavailable: 4,
  other: 5,
};

function hashString(seed: string): number {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = (hash << 5) - hash + seed.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash);
}

export function sortStatusDetails(details: StatusDetailEntry[]): StatusDetailEntry[] {
  return [...details].sort((a, b) => {
    const oa = DETAIL_PARENT_ORDER[a.parent_category] ?? 99;
    const ob = DETAIL_PARENT_ORDER[b.parent_category] ?? 99;
    if (oa !== ob) return oa - ob;
    return b.seconds - a.seconds;
  });
}

export function detailChartLabel(d: StatusDetailEntry): string {
  return d.detail_label;
}

export function detailTooltipLabel(d: StatusDetailEntry): string {
  const parent = categoryLabel(d.parent_category);
  const label = d.detail_label.trim();
  if (!label || label.toLowerCase() === parent.toLowerCase()) return parent;
  return `${parent} — ${label}`;
}

/**
 * High-contrast palette for detail segments (bar/pie must be distinguishable at a glance).
 * Inbound vs outbound use different hues, not two shades of red.
 */
const KNOWN_DETAIL_COLORS: Record<string, string> = {
  'available:available': '#4ade80',
  'talking:on-call-inbound': '#f43f5e',
  'talking:on-call-outbound': '#38bdf8',
  'wrap:wrap': '#fb923c',
  'paused:acw-after-call-work': '#a78bfa',
  'paused:ad-hoc-break': '#2dd4bf',
  'paused:lunch': '#fbbf24',
  'paused:training': '#818cf8',
  'unavailable:unavailable': '#facc15',
  'other:other': '#94a3b8',
};

/** Distinct hue per detail; known keys use a fixed palette. */
export function detailColor(
  parentCategory: string,
  detailKey: string,
  detailLabel?: string
): string {
  const known = KNOWN_DETAIL_COLORS[`${parentCategory}:${detailKey}`];
  if (known) return known;
  const seed = `${parentCategory}\0${detailKey}\0${detailLabel ?? ""}`;
  const hash = hashString(seed);
  const hue = hash % 360;
  const sat = 58 + (hash % 22);
  const light = 46 + ((hash >> 8) % 16);
  return `hsl(${hue}, ${sat}%, ${light}%)`;
}

export function detailDataKey(d: StatusDetailEntry): string {
  return `d_${d.parent_category}_${d.detail_key}`;
}
